import { type JSX, useCallback } from 'react';

import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { PREVIEW_CLOSE_EVENT, SUGGESTED_TEMPLATE_NOTICE } from '../../../../constants';
import { useCreatePetitionUpdateContext } from '../../context';
import { PreviewEmailButton } from '../PreviewEmailButton/index_new';

import { PreviewEmailModalBody } from './components/PreviewEmailModalBody';

type PreviewModalProps = {
	disabled: boolean;
	onPreview: () => void;
	emailTitle: string;
	emailContent: string;
	emailImageUrl?: string;
	petitionTitle: string;
	userName: string;
};

export function PreviewModal({
	disabled,
	onPreview,
	emailTitle,
	emailContent,
	emailImageUrl,
	petitionTitle,
	userName,
}: PreviewModalProps): JSX.Element | null {
	const {
		data: {
			featureConfigs: { petitionUpdateEmailPreviewEnabled },
		},
	} = useStarterDashboardV2PageData();
	const track = useTracking();

	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const onCloseModal = useCallback(() => {
		void track(PREVIEW_CLOSE_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			victory: false,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);
	const { translate } = useI18n();

	if (!petitionUpdateEmailPreviewEnabled) {
		return null;
	}

	return (
		<Modal
			data-testid="modal-overlay"
			onClose={onCloseModal}
			trigger={
				<PreviewEmailButton data-qa="petition-update-preview-email-button" disabled={disabled} onClick={onPreview} />
			}
		>
			<ModalHeader
				title={translate('corgi.petition.starter_dashboard_v2.post_an_update.preview.title')}
				subtitle={translate('corgi.petition.starter_dashboard_v2.post_an_update.preview.subtitle')}
			/>

			<ModalBody>
				<PreviewEmailModalBody
					emailTitle={emailTitle}
					emailContent={emailContent}
					imageDataUrl={emailImageUrl}
					petitionTitle={petitionTitle}
					userName={userName}
				/>
			</ModalBody>
		</Modal>
	);
}
